import { Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { POSItem } from '@/types';
import { resolveFileUrl } from '@/utils/fileUrl';
import { formatMoney } from '@/utils/currency';
import { getItemPrice } from '@/utils/posItemPricing';

interface Props {
  favorites: POSItem[];
  customerType: string;
  onSelect: (item: POSItem) => void;
  onToggleFavorite: (itemName: string) => void;
  isFavorite: (itemName: string) => boolean;
  showFavorites?: boolean;
  /** When empty, only List view shows the hint; Grid/Detail hide the section. */
  showEmptyHint?: boolean;
}

export default function PosQuickAccessStrip({
  favorites,
  customerType,
  onSelect,
  onToggleFavorite,
  isFavorite,
  showFavorites = true,
  showEmptyHint = false,
}: Props) {
  const { t } = useTranslation();

  if (!showFavorites) return null;
  if (favorites.length === 0 && !showEmptyHint) return null;

  const renderChip = (item: POSItem) => (
    <div key={`favorite-${item.name}`} className="relative shrink-0">
      <button
        type="button"
        onClick={() => onSelect(item)}
        className="flex flex-col items-center w-20 p-2 rounded-xl border border-gray-200 bg-white hover:border-primary-300 hover:shadow-sm transition-all min-h-[88px]"
      >
        <div className="w-12 h-12 rounded-lg bg-gray-100 overflow-hidden mb-1">
          {item.image ? (
            <img src={resolveFileUrl(item.image)} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-xs text-gray-400">—</div>
          )}
        </div>
        <span className="text-[11px] font-medium text-gray-800 line-clamp-2 text-center leading-tight">
          {item.item_name}
        </span>
        <span className="text-[10px] text-primary-600 font-semibold">
          {formatMoney(getItemPrice(item, customerType))}
        </span>
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggleFavorite(item.name);
        }}
        className="absolute -top-1 -right-1 p-0.5 bg-white rounded-full shadow border border-gray-100"
        aria-label={t('pos.toggleFavorite')}
      >
        <Star className={`w-3.5 h-3.5 ${isFavorite(item.name) ? 'fill-amber-400 text-amber-500' : 'text-gray-300'}`} />
      </button>
    </div>
  );

  return (
    <div className="mb-3">
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 px-1">
        {t('pos.favorites')}
      </div>
      {favorites.length > 0 ? (
        <div className="flex gap-2 overflow-x-auto pb-1">{favorites.map(renderChip)}</div>
      ) : (
        <p className="text-xs text-gray-400 px-1 py-2">{t('pos.noFavoritesHint')}</p>
      )}
    </div>
  );
}
